import {
  Box,
  Divider,
  Link,
  List,
  ListItem,
  Skeleton,
  Typography,
  Card,
  useMediaQuery,
  useTheme,
  InputAdornment,
  TextField,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Grid,
} from '@mui/material';
import PageHeader from '../components/Page/PageHeader';
import ErrorView from '../components/ErrorViews/ErrorView';
import { Link as RouterLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import BecomeAWriter from '../components/Promotional/BecomeAWriter';
import { getRouterPathToAuthor } from '../utilities/Links';
import { useCallback, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { PersonOutlined, PersonSearchOutlined } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import OpenGraphGeneral from '../components/MetaData/OpenGraphGeneral';
import { useAppSelector } from '../store/hooks';
import { selectAuthors } from '../store/authors';
import { RootState } from '../store/store';
import useDocumentTitle from '../hooks/useDocumentTitle';

export default function AuthorListPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('authors.title'));
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const authors = useAppSelector(selectAuthors);
  const loading = useAppSelector((state: RootState) => state.authors.loading);
  const error = useAppSelector((state: RootState) => state.authors.error);

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    },
    [],
  );
  const closeSearchModal = useCallback(() => {
    setSearchTerm('');
    setOpen(false);
  }, []);

  const openSearchModal = useCallback(() => {
    setOpen(true);
  }, []);

  const filteredAuthors = authors?.filter((author) =>
    author.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (error) {
    return <ErrorView apiError={error} />;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader title={t('authors.title')} />

      <Grid container>
        <Grid size={{ xs: 12, md: 10 }}>
          <Box
            sx={{
              mb: 4,
            }}
          >
            <Typography
              variant='body1'
              sx={{
                color: 'text.primary',
              }}
            >
              {t('authors.description')}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Card variant='outlined' sx={{ padding: 5 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 2,
          }}
        >
          <Typography variant='h2' sx={{ paddingY: 2 }}>
            {t('authors.titleLong')}
          </Typography>

          {isMobile ? (
            <Button
              size='large'
              variant='text'
              color='primary'
              startIcon={<PersonSearchOutlined />}
              onClick={openSearchModal}
            >
              {t('authors.search')}
            </Button>
          ) : (
            <TextField
              id='outlined-search'
              label={t('authors.searchLabel')}
              type='search'
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>
        {loading ? (
          <List sx={{ width: '100%' }}>
            {Array.from({ length: 20 }).map((_, index) => (
              <Box key={index}>
                <ListItem sx={{ paddingLeft: '0px' }}>
                  <Skeleton variant='text' width='100%' />
                </ListItem>
              </Box>
            ))}
          </List>
        ) : (
          <List sx={{ width: '100%' }}>
            {filteredAuthors
              ?.slice()
              .sort()
              .map((author) => (
                <Box key={'author-list-' + author}>
                  <ListItem disablePadding>
                    <Link
                      variant='body1'
                      component={RouterLink}
                      to={getRouterPathToAuthor(author)}
                      sx={{
                        paddingY: 1,
                        color: 'text.primary',
                        width: '100%',
                        textDecoration: 'none',
                        '&:hover': {
                          backgroundColor: 'background.default',
                        },
                      }}
                    >
                      {author}
                    </Link>
                  </ListItem>
                  <Divider />
                </Box>
              ))}
          </List>
        )}
      </Card>
      <Box sx={{ marginTop: 4 }}>
        <BecomeAWriter />
      </Box>

      <Dialog fullScreen open={open} sx={{ zIndex: 1302 }}>
        <Typography
          variant='h2'
          sx={{
            paddingLeft: 2,
            paddingY: 1,
          }}
        >
          {t('authors.searchTitle')}
        </Typography>
        <IconButton
          sx={{ position: 'absolute', top: 10, right: 10 }}
          onClick={closeSearchModal}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <TextField
            label={t('authors.searchLabel')}
            value={searchTerm}
            onChange={handleSearch}
            fullWidth
            helperText={t('authors.searchHelper')}
          />
          <List sx={{ width: '100%' }}>
            {filteredAuthors
              ?.slice()
              .sort()
              .map((author) => (
                <Box key={'author-list-' + author}>
                  <ListItem disablePadding>
                    <PersonOutlined sx={{ marginRight: '8px' }} />
                    <Link
                      variant='body1'
                      component={RouterLink}
                      to={getRouterPathToAuthor(author)}
                      sx={{
                        paddingY: 1,
                        color: 'text.primary',
                        width: '100%',
                        textDecoration: 'none',
                        '&:hover': {
                          backgroundColor: 'background.default',
                        },
                      }}
                    >
                      {author}
                    </Link>
                  </ListItem>
                  <Divider />
                </Box>
              ))}
          </List>
        </DialogContent>
      </Dialog>
      <OpenGraphGeneral />
    </Box>
  );
}
